import { useEffect, useRef } from 'react';

import {
  type EmbeddingJob,
  getCigarEmbeddingJob,
  isEmbeddingJobActive,
} from '@/api/cigar-matching';

export const EMBEDDING_JOB_POLL_INTERVAL_MS = 3000;

export const useEmbeddingJobPolling = (
  job: EmbeddingJob | null,
  onUpdate: (job: EmbeddingJob) => void,
  onError: (error: unknown) => void
) => {
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!job?.id || !isEmbeddingJobActive(job)) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const response = await getCigarEmbeddingJob(job.id);
        if (cancelled) return;
        onUpdateRef.current(response.job);
        if (isEmbeddingJobActive(response.job)) {
          timer = setTimeout(poll, EMBEDDING_JOB_POLL_INTERVAL_MS);
        }
      } catch (error) {
        if (cancelled) return;
        onErrorRef.current(error);
        timer = setTimeout(poll, EMBEDDING_JOB_POLL_INTERVAL_MS);
      }
    };

    timer = setTimeout(poll, EMBEDDING_JOB_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [job?.id, job?.status]);
};
